import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, LogOut, X, Mail, ExternalLink, Flower2, Download, Upload, FileText } from "lucide-react";
import AdminResources from "@/pages/admin/AdminResources";
import AdminTeam from "@/pages/admin/AdminTeam";
import AdminClubs from "@/pages/admin/AdminClubs";
import AdminTalleres from "@/pages/admin/AdminTalleres";
import AdminAlianzas from "@/pages/admin/AdminAlianzas";
import AdminFaqs from "@/pages/admin/AdminFaqs";

const empty = { title: "", excerpt: "", url: "", image_url: "", tag: "Blog" };

const Dashboard = () => {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("blog");
  const [posts, setPosts] = useState([]);
  const [messages, setMessages] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [volumes, setVolumes] = useState([]);
  const [volForm, setVolForm] = useState({ label: "", title: "", season: "" });
  const [volFile, setVolFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [editingVolId, setEditingVolId] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  useEffect(() => {
    if (!loading && !user) navigate("/admin/login");
  }, [loading, user, navigate]);

  const loadPosts = () => api.get("/blog").then((r) => setPosts(r.data)).catch(() => {});
  const loadMessages = () => api.get("/contact").then((r) => setMessages(r.data)).catch(() => {});
  const loadSubscribers = () => api.get("/subscribe").then((r) => setSubscribers(r.data)).catch(() => {});
  const loadVolumes = () => api.get("/newsletter/volumes").then((r) => setVolumes(r.data)).catch(() => {});

  useEffect(() => {
    if (user) {
      loadPosts();
      loadMessages();
      loadSubscribers();
      loadVolumes();
    }
  }, [user]);

  const exportCsv = () => {
    const rows = [
      ["Nombre", "Correo", "Fecha"],
      ...subscribers.map((s) => [s.name || "", s.email, s.created_at]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "suscriptores-el-jardin.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const uploadVolume = async (e) => {
    e.preventDefault();
    if (!editingVolId && !volFile) {
      toast.error("Selecciona un archivo PDF");
      return;
    }
    const fd = new FormData();
    fd.append("label", volForm.label);
    fd.append("title", volForm.title);
    fd.append("season", volForm.season);
    if (volFile) fd.append("file", volFile);
    setUploading(true);
    try {
      if (editingVolId) {
        await api.put(`/newsletter/volumes/${editingVolId}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Volumen actualizado");
      } else {
        await api.post("/newsletter/volumes", fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Volumen publicado");
      }
      setVolForm({ label: "", title: "", season: "" });
      setVolFile(null);
      setEditingVolId(null);
      loadVolumes();
    } catch {
      toast.error("No se pudo guardar el volumen");
    } finally {
      setUploading(false);
    }
  };

  const editVolume = (v) => {
    setEditingVolId(v.id);
    setVolForm({ label: v.label || "", title: v.title || "", season: v.season || "" });
    setVolFile(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const cancelVolEdit = () => { setEditingVolId(null); setVolForm({ label: "", title: "", season: "" }); setVolFile(null); };

  const deleteVolume = async (id) => {
    if (!window.confirm("¿Eliminar este volumen?")) return;
    await api.delete(`/newsletter/volumes/${id}`);
    toast.success("Volumen eliminado");
    loadVolumes();
  };

  const openNew = () => {
    setForm(empty);
    setEditing("new");
  };
  const openEdit = (p) => {
    setForm({ title: p.title, excerpt: p.excerpt, url: p.url, image_url: p.image_url, tag: p.tag });
    setEditing(p.id);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing === "new") {
        await api.post("/blog", form);
        toast.success("Entrada creada");
      } else {
        await api.put(`/blog/${editing}`, form);
        toast.success("Entrada actualizada");
      }
      setEditing(null);
      loadPosts();
    } catch {
      toast.error("No se pudo guardar");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar esta entrada?")) return;
    await api.delete(`/blog/${id}`);
    toast.success("Entrada eliminada");
    loadPosts();
  };

  if (loading || !user) return null;

  return (
    <div className="min-h-screen bg-brand-cream">
      <header className="border-b border-[#E5E5E5] bg-white">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="font-heading font-black text-xl tracking-tighter text-brand-ink">
            Juventudes <span className="text-gradient">Latentes</span>
          </Link>
          <button
            data-testid="admin-logout"
            onClick={() => {
              logout();
              navigate("/admin/login");
            }}
            className="inline-flex items-center gap-2 text-sm font-semibold text-brand-ink hover:text-brand-magenta"
          >
            <LogOut size={16} /> Salir
          </button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-10">
        <h1 className="font-heading text-3xl font-bold tracking-tight text-brand-ink">Panel</h1>

        <div className="mt-6 flex gap-2 flex-wrap">
          <button
            data-testid="tab-blog"
            onClick={() => setTab("blog")}
            className={`px-4 py-2 rounded-sm text-sm font-semibold ${tab === "blog" ? "bg-brand-ink text-white" : "bg-white border border-[#E5E5E5] text-brand-ink"}`}
          >
            Blog ({posts.length})
          </button>
          <button
            data-testid="tab-messages"
            onClick={() => setTab("messages")}
            className={`px-4 py-2 rounded-sm text-sm font-semibold ${tab === "messages" ? "bg-brand-ink text-white" : "bg-white border border-[#E5E5E5] text-brand-ink"}`}
          >
            Mensajes ({messages.length})
          </button>
          <button
            data-testid="tab-subscribers"
            onClick={() => setTab("subscribers")}
            className={`px-4 py-2 rounded-sm text-sm font-semibold ${tab === "subscribers" ? "bg-brand-ink text-white" : "bg-white border border-[#E5E5E5] text-brand-ink"}`}
          >
            Suscriptores ({subscribers.length})
          </button>
          <button
            data-testid="tab-jardin"
            onClick={() => setTab("jardin")}
            className={`px-4 py-2 rounded-sm text-sm font-semibold ${tab === "jardin" ? "bg-brand-ink text-white" : "bg-white border border-[#E5E5E5] text-brand-ink"}`}
          >
            El Jardín ({volumes.length})
          </button>
          <button
            data-testid="tab-recursos"
            onClick={() => setTab("recursos")}
            className={`px-4 py-2 rounded-sm text-sm font-semibold ${tab === "recursos" ? "bg-brand-ink text-white" : "bg-white border border-[#E5E5E5] text-brand-ink"}`}
          >
            Recursos
          </button>
          <button
            data-testid="tab-equipo"
            onClick={() => setTab("equipo")}
            className={`px-4 py-2 rounded-sm text-sm font-semibold ${tab === "equipo" ? "bg-brand-ink text-white" : "bg-white border border-[#E5E5E5] text-brand-ink"}`}
          >
            Equipo
          </button>
          <button
            data-testid="tab-clubs"
            onClick={() => setTab("clubs")}
            className={`px-4 py-2 rounded-sm text-sm font-semibold ${tab === "clubs" ? "bg-brand-ink text-white" : "bg-white border border-[#E5E5E5] text-brand-ink"}`}
          >
            Clubs
          </button>
          <button
            data-testid="tab-talleres"
            onClick={() => setTab("talleres")}
            className={`px-4 py-2 rounded-sm text-sm font-semibold ${tab === "talleres" ? "bg-brand-ink text-white" : "bg-white border border-[#E5E5E5] text-brand-ink"}`}
          >
            Talleres
          </button>
          <button
            data-testid="tab-alianzas"
            onClick={() => setTab("alianzas")}
            className={`px-4 py-2 rounded-sm text-sm font-semibold ${tab === "alianzas" ? "bg-brand-ink text-white" : "bg-white border border-[#E5E5E5] text-brand-ink"}`}
          >
            Alianzas
          </button>
          <button
            data-testid="tab-faqs"
            onClick={() => setTab("faqs")}
            className={`px-4 py-2 rounded-sm text-sm font-semibold ${tab === "faqs" ? "bg-brand-ink text-white" : "bg-white border border-[#E5E5E5] text-brand-ink"}`}
          >
            FAQs
          </button>
        </div>

        {tab === "recursos" && <AdminResources />}
        {tab === "equipo" && <AdminTeam />}
        {tab === "clubs" && <AdminClubs />}
        {tab === "talleres" && <AdminTalleres />}
        {tab === "alianzas" && <AdminAlianzas />}
        {tab === "faqs" && <AdminFaqs />}

        {tab === "blog" && (
          <div className="mt-8">
            <button
              data-testid="new-post-btn"
              onClick={openNew}
              className="inline-flex items-center gap-2 bg-brand-magenta text-white font-semibold px-4 py-2.5 rounded-sm hover:opacity-90"
            >
              <Plus size={18} /> Nueva entrada
            </button>

            <div className="mt-6 grid grid-cols-1 gap-3">
              {posts.map((p) => (
                <div
                  key={p.id}
                  data-testid={`admin-post-${p.id}`}
                  className="bg-white border border-[#E5E5E5] rounded-sm p-4 flex items-center gap-4"
                >
                  {p.image_url && (
                    <img src={p.image_url} alt="" className="w-16 h-16 object-cover rounded-sm" />
                  )}
                  <div className="flex-1 min-w-0">
                    <span className="text-xs font-bold bg-brand-lime px-2 py-0.5 rounded-sm">{p.tag}</span>
                    <h3 className="mt-1 font-semibold text-brand-ink truncate">{p.title}</h3>
                    <a href={p.url} target="_blank" rel="noopener noreferrer" className="text-xs text-brand-magenta inline-flex items-center gap-1 truncate">
                      {p.url} <ExternalLink size={11} />
                    </a>
                  </div>
                  <div className="flex gap-2">
                    <button data-testid={`edit-post-${p.id}`} onClick={() => openEdit(p)} className="p-2 text-brand-ink hover:text-brand-orange">
                      <Pencil size={17} />
                    </button>
                    <button data-testid={`delete-post-${p.id}`} onClick={() => remove(p.id)} className="p-2 text-brand-ink hover:text-red-600">
                      <Trash2 size={17} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "messages" && (
          <div className="mt-8 grid grid-cols-1 gap-3">
            {messages.length === 0 && <p className="text-brand-ink/50">Aún no hay mensajes.</p>}
            {messages.map((m) => (
              <div key={m.id} data-testid={`admin-message-${m.id}`} className="bg-white border border-[#E5E5E5] rounded-sm p-5">
                <div className="flex items-center gap-2 text-sm flex-wrap">
                  <Mail size={15} className="text-brand-magenta" />
                  <span className="font-semibold text-brand-ink">{m.name}</span>
                  <a href={`mailto:${m.email}`} className="text-brand-ink/50 hover:text-brand-magenta">· {m.email}</a>
                  {m.created_at && (
                    <span className="text-xs text-brand-ink/40 ml-auto">
                      {new Date(m.created_at).toLocaleString("es-MX", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                    </span>
                  )}
                </div>
                {m.subject && <p className="mt-2 font-semibold text-brand-ink">{m.subject}</p>}
                <p className="mt-1 text-brand-ink/70 whitespace-pre-wrap">{m.message}</p>
              </div>
            ))}
          </div>
        )}


        {tab === "subscribers" && (
          <div className="mt-8">
            {subscribers.length > 0 && (
              <button
                data-testid="export-subscribers-csv"
                onClick={exportCsv}
                className="mb-4 inline-flex items-center gap-2 bg-brand-lime text-brand-ink font-semibold px-4 py-2.5 rounded-sm hover:opacity-90"
              >
                <Download size={17} /> Exportar CSV
              </button>
            )}
            {subscribers.length === 0 ? (
              <p className="text-brand-ink/50">Aún no hay suscriptores a El Jardín.</p>
            ) : (
              <div className="bg-white border border-[#E5E5E5] rounded-sm overflow-hidden">
                {subscribers.map((s) => (
                  <div key={s.id} data-testid={`admin-subscriber-${s.id}`} className="flex items-center gap-3 px-5 py-3.5 border-b border-[#F0F0F0] last:border-0">
                    <Flower2 size={16} className="text-brand-magenta flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      {s.name && <span className="font-semibold text-brand-ink mr-2">{s.name}</span>}
                      <span className="text-brand-ink/70 break-all">{s.email}</span>
                    </div>
                    <span className="text-xs text-brand-ink/40 whitespace-nowrap">
                      {new Date(s.created_at).toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" })}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "jardin" && (
          <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Formulario de subida */}
            <form onSubmit={uploadVolume} data-testid="volume-form" className="bg-white border border-[#E5E5E5] rounded-sm p-6 h-fit">
              <div className="flex items-center justify-between">
                <h2 className="font-heading text-lg font-bold text-brand-ink flex items-center gap-2">
                  <Upload size={18} className="text-brand-magenta" /> {editingVolId ? "Editar volumen" : "Nuevo volumen"}
                </h2>
                {editingVolId && <button type="button" onClick={cancelVolEdit} data-testid="volume-cancel" className="text-sm text-brand-ink/60 hover:text-brand-ink">Cancelar</button>}
              </div>
              <p className="mt-1 text-sm text-brand-ink/60">Sube el PDF; la portada se genera automáticamente.</p>
              <div className="mt-5 space-y-4">
                <div>
                  <label className="text-sm font-semibold text-brand-ink">Etiqueta (ej. Vol. 2)</label>
                  <input required data-testid="volume-label" value={volForm.label} onChange={(e) => setVolForm({ ...volForm, label: e.target.value })} className="mt-1.5 w-full border border-[#E5E5E5] rounded-sm px-4 py-2.5 outline-none focus:border-brand-magenta" />
                </div>
                <div>
                  <label className="text-sm font-semibold text-brand-ink">Título</label>
                  <input required data-testid="volume-title" value={volForm.title} onChange={(e) => setVolForm({ ...volForm, title: e.target.value })} className="mt-1.5 w-full border border-[#E5E5E5] rounded-sm px-4 py-2.5 outline-none focus:border-brand-magenta" />
                </div>
                <div>
                  <label className="text-sm font-semibold text-brand-ink">Temporada / fecha (opcional)</label>
                  <input data-testid="volume-season" value={volForm.season} onChange={(e) => setVolForm({ ...volForm, season: e.target.value })} className="mt-1.5 w-full border border-[#E5E5E5] rounded-sm px-4 py-2.5 outline-none focus:border-brand-magenta" />
                </div>
                <div>
                  <label className="text-sm font-semibold text-brand-ink">Archivo PDF{editingVolId ? " — opcional (déjalo vacío para conservar el actual)" : ""}</label>
                  <input type="file" accept="application/pdf" data-testid="volume-file" onChange={(e) => setVolFile(e.target.files[0])} className="mt-1.5 w-full text-sm border border-[#E5E5E5] rounded-sm px-4 py-2.5 file:mr-3 file:py-1.5 file:px-3 file:rounded-sm file:border-0 file:bg-brand-ink file:text-white" />
                </div>
              </div>
              <button type="submit" disabled={uploading} data-testid="upload-volume-btn" className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-brand-magenta text-white font-semibold py-3 rounded-sm hover:bg-brand-orange transition-colors disabled:opacity-60">
                {uploading ? "Guardando…" : editingVolId ? "Guardar cambios" : "Publicar volumen"}
              </button>
            </form>

            {/* Lista de volúmenes */}
            <div>
              <h2 className="font-heading text-lg font-bold text-brand-ink mb-4">Volúmenes publicados</h2>
              {volumes.length === 0 ? (
                <p className="text-brand-ink/50">Aún no hay volúmenes.</p>
              ) : (
                <div className="grid grid-cols-1 gap-3">
                  {volumes.map((v) => (
                    <div key={v.id} data-testid={`admin-volume-${v.id}`} className="bg-white border border-[#E5E5E5] rounded-sm p-4 flex items-center gap-3">
                      <FileText size={20} className="text-brand-magenta flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <span className="text-xs font-bold bg-brand-yellow px-2 py-0.5 rounded-sm">{v.label}</span>
                        <h3 className="mt-1 font-semibold text-brand-ink truncate">{v.title}</h3>
                        {v.season && <p className="text-xs text-brand-ink/50">{v.season}</p>}
                      </div>
                      <button data-testid={`edit-volume-${v.id}`} onClick={() => editVolume(v)} className="p-2 text-brand-ink hover:text-brand-magenta">
                        <Pencil size={16} />
                      </button>
                      {!v.is_static && (
                        <button data-testid={`delete-volume-${v.id}`} onClick={() => deleteVolume(v.id)} className="p-2 text-brand-ink hover:text-red-600">
                          <Trash2 size={17} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-6">
          <form
            onSubmit={save}
            data-testid="post-form"
            className="bg-white rounded-sm w-full max-w-lg p-7 max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-xl font-bold text-brand-ink">
                {editing === "new" ? "Nueva entrada" : "Editar entrada"}
              </h2>
              <button type="button" data-testid="close-modal" onClick={() => setEditing(null)}>
                <X size={22} />
              </button>
            </div>
            <div className="mt-5 space-y-4">
              {[
                { k: "title", label: "Título" },
                { k: "excerpt", label: "Resumen" },
                { k: "url", label: "Enlace externo (URL)" },
                { k: "image_url", label: "URL de imagen" },
                { k: "tag", label: "Etiqueta" },
              ].map((f) => (
                <div key={f.k}>
                  <label className="text-sm font-semibold text-brand-ink">{f.label}</label>
                  <input
                    required={f.k === "title" || f.k === "url"}
                    data-testid={`post-${f.k}`}
                    value={form[f.k]}
                    onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                    className="mt-1.5 w-full border border-[#E5E5E5] rounded-sm px-4 py-2.5 outline-none focus:border-brand-magenta"
                  />
                </div>
              ))}
            </div>
            <button
              type="submit"
              data-testid="save-post"
              className="mt-6 w-full bg-brand-ink text-white font-semibold py-3 rounded-sm hover:bg-brand-orange transition-colors"
            >
              Guardar
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
